import React, { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import HomePage from './components/HomePage';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { CookieBanner, CookiePreferencesModal } from './components/CookieConsent';

// Lazy load non-critical pages for better initial load performance
const AgentPage = lazy(() => import('./components/AgentPage'));
const FeaturesPage = lazy(() => import('./components/FeaturesPage'));
// Use Cases area (CP4) — 3 department pages + 3 workflow pages (no hub; the two-column
// mega-menu is the entry point). The two page templates are each driven by a slug prop
// (like AgentPage). All routes are registered ABOVE the /:slug catch-all below (D2).
const UseCaseDepartmentPage = lazy(() => import('./components/UseCaseDepartmentPage'));
const UseCaseWorkflowPage = lazy(() => import('./components/UseCaseWorkflowPage'));
const PricingPage = lazy(() => import('./components/PricingPage'));
// Services (CP5) — the Dubai service page generalized + de-localized into a REAL indexed
// page with the global header (NOT LandingLayout, D3). Nav label + slug = "Services" /
// /services (user, 2026-08-25 — supersedes O4's "Work with us"). Single-segment slug, so
// it MUST stay above the /:slug catch-all below (D2).
const ServicesPage = lazy(() => import('./components/ServicesPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const FAQPage = lazy(() => import('./components/FAQPage'));
const BlogListPage = lazy(() => import('./components/BlogListPage'));
const BlogPostPage = lazy(() => import('./components/BlogPostPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AdminEditor = lazy(() => import('./components/AdminEditor'));
// Paid-search landing pages (noindex, distraction-free) — registered ABOVE the /:slug
// catch-all so their single-segment slugs are not swallowed by the blog route.
const AiAutomationWorkflowsPage = lazy(() => import('./components/landing/AiAutomationWorkflowsPage'));
const AiAgentsPage = lazy(() => import('./components/landing/AiAgentsPage'));
// Service landing page (noindex, distraction-free) — sells the done-for-you
// revenue-recovery service. Receives paid search on AI-category keywords (ad group:
// "ai agency" / "ai consultancy" / "ai development companies") and is also shared
// directly over WhatsApp and email. File/component keep the RevenueRecovery name —
// that's the offer; /ai-agency-dubai is the slug the ad group needs.
const RevenueRecoveryPage = lazy(() => import('./components/landing/RevenueRecoveryPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="w-8 h-8 border-2 border-gray-300 border-t-coral-500 rounded-full animate-spin" 
         style={{ borderTopColor: '#FF6B6B' }} />
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  // Distraction-free paid-search landing pages: suppress the nav Header (it carries a
  // competing "Get Started" CTA). Cookie banner is intentionally KEPT below.
  const isBareLanding = ['/ai-automation-workflows', '/ai-agents', '/ai-agency-dubai'].includes(
    location.pathname
  );
  return (
    <>
      {!isAdmin && !isBareLanding && <Header />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* How It Works (was "Features", user 2026-08-25) — capability story (CP3).
              Component kept as FeaturesPage (content = features). Single-segment slug, so
              it MUST stay above the /:slug catch-all below (D2). */}
          <Route path="/how-it-works" element={<FeaturesPage />} />
          {/* Use Cases area (CP4). 3 workflow + 3 department pages (no hub) — the
              /use-cases/* paths are two-segment, so they sit safely above /:slug. */}
          <Route path="/use-cases/crm-management" element={<UseCaseWorkflowPage workflowSlug="crm-management" />} />
          <Route path="/use-cases/pipeline-generation" element={<UseCaseWorkflowPage workflowSlug="pipeline-generation" />} />
          <Route path="/use-cases/focus-and-flow" element={<UseCaseWorkflowPage workflowSlug="focus-and-flow" />} />
          <Route path="/use-cases/sales" element={<UseCaseDepartmentPage departmentSlug="sales" />} />
          <Route path="/use-cases/marketing" element={<UseCaseDepartmentPage departmentSlug="marketing" />} />
          <Route path="/use-cases/personal-productivity" element={<UseCaseDepartmentPage departmentSlug="personal-productivity" />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* Services — indexed service page (CP5). Single segment → above /:slug (D2). */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutPage />} />
          {/* Individual Agent Pages - Standalone URLs with "ai" in path */}
          <Route path="/buzz-ai-social-media-expert" element={<AgentPage agentSlug="buzz" />} />
          <Route path="/dora-ai-research-data-analyst" element={<AgentPage agentSlug="dora" />} />
          <Route path="/finn-ai-finance-expert" element={<AgentPage agentSlug="finn" />} />
          <Route path="/luc-ai-personal-growth-partner" element={<AgentPage agentSlug="luc" />} />
          <Route path="/marsi-ai-marketing-growth-expert" element={<AgentPage agentSlug="marsi" />} />
          <Route path="/sally-ai-sales-expert" element={<AgentPage agentSlug="sally" />} />
          <Route path="/stark-ai-business-strategist" element={<AgentPage agentSlug="stark" />} />
          <Route path="/tali-ai-hr-people-partner" element={<AgentPage agentSlug="tali" />} />
          <Route path="/wright-ai-copywriting-expert" element={<AgentPage agentSlug="wright" />} />
          <Route path="/zira-ai-support-expert" element={<AgentPage agentSlug="zira" />} />
          {/* Old URL redirects handled by Cloudflare 301 rules for proper SEO */}
          {/* Placeholder routes for Resources */}
          <Route path="/blog" element={<BlogListPage />} />
          {/* Legacy /blog/:slug → 301-equivalent redirect to /:slug (SEO continuity) */}
          <Route path="/blog/:slug" element={<BlogSlugRedirect />} />
          <Route path="/faq" element={<FAQPage />} />
          {/* Paid-search landing pages — MUST stay above the /:slug catch-all below */}
          <Route path="/ai-automation-workflows" element={<AiAutomationWorkflowsPage />} />
          <Route path="/ai-agents" element={<AiAgentsPage />} />
          {/* Service landing page — also a single segment, so it MUST stay above /:slug */}
          <Route path="/ai-agency-dubai" element={<RevenueRecoveryPage />} />
          <Route path="/help" element={<ComingSoon title="Help Center" />} />
          {/* Legal Pages */}
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          {/* Admin CMS Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/editor" element={<AdminEditor />} />
          <Route path="/admin/editor/:id" element={<AdminEditor />} />
          {/* Blog article slug at root (must come AFTER all specific routes) */}
          <Route path="/:slug" element={<BlogPostPage />} />
          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {!isAdmin && (
        <>
          <CookieBanner />
          <CookiePreferencesModal />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <CookieConsentProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout />
      </BrowserRouter>
    </div>
    </CookieConsentProvider>
    </HelmetProvider>
  );
}

// Redirect legacy /blog/:slug → /:slug (preserves SEO via Helmet canonical + server-side 301 from Cloudflare in prod)
function BlogSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}

// Simple placeholder component for coming soon pages
function ComingSoon({ title }) {
  return (
    <div className="min-h-screen aurora-gradient flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-5xl font-light text-white mb-4">{title}</h1>
        <p className="text-white/60 mb-8">This page is coming soon.</p>
        <a 
          href="/" 
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white"
          style={{ backgroundColor: '#FF5C5C' }}
        >
          Back to Home
        </a>
      </div>
    </div>
  );
}

export default App;
